import { supabase } from '@/lib/supabase';

// Event Booking Types
export interface EventBooking {
  id?: string;
  reference_number?: string;
  status?: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  event_type: 'private' | 'corporate' | 'wedding' | 'birthday' | 'other';
  event_date: string;
  event_time: string;
  duration: string;
  guest_count: number;
  location_type: string;
  address: string;
  postal_code: string;
  city: string;
  menu_package: 'classico' | 'premium' | 'deluxe';
  special_requests?: string;
  dietary_needs?: string[];
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company?: string;
  preferred_contact: string;
  newsletter_subscribed?: boolean;
  admin_notes?: string;
  responded_at?: string;
  created_at?: string;
  updated_at?: string;
}

// Contact Inquiry Types
export interface ContactInquiry {
  id?: string;
  status?: 'new' | 'in_progress' | 'responded' | 'closed';
  name: string;
  email: string;
  phone?: string;
  company?: string;
  subject: string;
  message: string;
  inquiry_type?: string;
  preferred_contact_method?: string;
  admin_response?: string;
  responded_by?: string;
  responded_at?: string;
  created_at?: string;
  updated_at?: string;
}

// Dashboard Statistics Types
export interface BookingStats {
  total_bookings: number;
  pending_bookings: number;
  confirmed_bookings: number;
  this_month_bookings: number;
}

export interface InquiryStats {
  total_inquiries: number;
  new_inquiries: number;
  responded_inquiries: number;
  this_week_inquiries: number;
}

// ============================================
// EVENT BOOKINGS SERVICE
// ============================================

/**
 * Create a new event booking
 */
export async function createEventBooking(booking: EventBooking) {
  try {
    // Generate reference number
    const timestamp = Date.now().toString().slice(-8);
    const reference = `TON-${timestamp}`;

    const { data, error } = await supabase
      .from('event_bookings')
      .insert([{ ...booking, reference_number: reference }])
      .select()
      .single();

    if (error) {
      console.error('Error creating booking:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Unexpected error creating booking:', error);
    return { data: null, error: error.message || 'Failed to create booking' };
  }
}

/**
 * Get all event bookings (admin only)
 */
export async function getAllEventBookings() {
  try {
    const { data, error } = await supabase
      .from('event_bookings')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching bookings:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Unexpected error fetching bookings:', error);
    return { data: null, error: error.message || 'Failed to fetch bookings' };
  }
}

/**
 * Get a single event booking by ID
 */
export async function getEventBookingById(id: string) {
  try {
    const { data, error } = await supabase
      .from('event_bookings')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching booking:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Unexpected error fetching booking:', error);
    return { data: null, error: error.message || 'Failed to fetch booking' };
  }
}

/**
 * Update event booking status and admin notes
 */
export async function updateEventBooking(
  id: string,
  updates: Partial<EventBooking>
) {
  try {
    const { data, error } = await supabase
      .from('event_bookings')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating booking:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Unexpected error updating booking:', error);
    return { data: null, error: error.message || 'Failed to update booking' };
  }
}

/**
 * Get booking statistics for admin dashboard
 */
export async function getBookingStats(): Promise<{
  data: BookingStats | null;
  error: string | null;
}> {
  try {
    const { data, error } = await supabase.rpc('get_booking_stats');

    if (error) {
      console.error('Error fetching booking stats:', error);
      return { data: null, error: error.message };
    }

    return { data: data?.[0] || null, error: null };
  } catch (error: any) {
    console.error('Unexpected error fetching booking stats:', error);
    return { data: null, error: error.message || 'Failed to fetch booking stats' };
  }
}

// ============================================
// CONTACT INQUIRIES SERVICE
// ============================================

/**
 * Create a new contact inquiry
 */
export async function createContactInquiry(inquiry: ContactInquiry) {
  try {
    const { data, error } = await supabase
      .from('contact_inquiries')
      .insert([inquiry])
      .select()
      .single();

    if (error) {
      console.error('Error creating inquiry:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Unexpected error creating inquiry:', error);
    return { data: null, error: error.message || 'Failed to create inquiry' };
  }
}

/**
 * Get all contact inquiries (admin only)
 */
export async function getAllContactInquiries() {
  try {
    const { data, error } = await supabase
      .from('contact_inquiries')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching inquiries:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Unexpected error fetching inquiries:', error);
    return { data: null, error: error.message || 'Failed to fetch inquiries' };
  }
}

/**
 * Update contact inquiry (add response, change status)
 */
export async function updateContactInquiry(
  id: string,
  updates: Partial<ContactInquiry>
) {
  try {
    const { data, error } = await supabase
      .from('contact_inquiries')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating inquiry:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Unexpected error updating inquiry:', error);
    return { data: null, error: error.message || 'Failed to update inquiry' };
  }
}

/**
 * Get inquiry statistics for admin dashboard
 */
export async function getInquiryStats(): Promise<{
  data: InquiryStats | null;
  error: string | null;
}> {
  try {
    const { data, error } = await supabase.rpc('get_inquiry_stats');

    if (error) {
      console.error('Error fetching inquiry stats:', error);
      return { data: null, error: error.message };
    }

    return { data: data?.[0] || null, error: null };
  } catch (error: any) {
    console.error('Unexpected error fetching inquiry stats:', error);
    return { data: null, error: error.message || 'Failed to fetch inquiry stats' };
  }
}